import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Container from '~/components/Container';
import Button from '~/components/Button';
import { login, register, useAuth } from '~/lib/auth';
import { useTitle } from '~/lib/useTitle';
import s from './Auth.module.scss';

// Only same-origin relative paths — blocks open-redirect through ?returnUrl.
function safeReturn(search: string): string {
  const r = new URLSearchParams(search).get('returnUrl');
  return r && r.startsWith('/') && !r.startsWith('//') ? r : '/account';
}

export default function Auth() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const profile = useAuth();
  const isRegister = pathname.startsWith('/register');
  const dest = safeReturn(search);

  useTitle(isRegister ? 'Create account — MetaHub' : 'Sign in — MetaHub');

  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (profile) navigate(dest, { replace: true });
  }, [profile, dest, navigate]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (pending) return;
    const data = new FormData(e.currentTarget);
    const email = String(data.get('email') || '').trim();
    const password = String(data.get('password') || '');

    if (!email || !password) return setError('Please fill in all fields.');
    if (isRegister) {
      if (password !== String(data.get('confirmPassword') || '')) return setError('Passwords do not match.');
      if (!data.get('tos') || !data.get('privacy')) return setError('Please accept the Terms and Privacy Policy.');
    }

    setError('');
    setPending(true);
    try {
      if (isRegister) {
        await register(email, password, { from: 'browser', tos: true, privacy: true, marketing: !!data.get('marketing') });
      } else {
        await login(email, password);
      }
      navigate(dest, { replace: true });
    } catch (err) {
      setError((err as Error).message || 'Something went wrong. Please try again.');
      setPending(false);
    }
  };

  return (
    <section className={s.wrap}>
      <Container>
        <div className={s.card}>
          <div className={s.kicker}>MetaHub</div>
          <h1>{isRegister ? 'Create your account' : 'Welcome back'}</h1>
          <p className={s.sub}>
            {isRegister ? 'Sign up to unlock the MetaHub API.' : 'Sign in to manage your MetaHub plan.'}
          </p>

          <form className={s.form} onSubmit={onSubmit} noValidate>
            <input name="email" type="email" placeholder="Email" autoComplete="email" required />
            <input
              name="password"
              type="password"
              placeholder="Password"
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              required
            />
            {isRegister && (
              <>
                <input name="confirmPassword" type="password" placeholder="Confirm password" autoComplete="new-password" required />
                <label className={s.check}>
                  <input type="checkbox" name="tos" /> I accept the{' '}
                  <a href="/terms" target="_blank" rel="noreferrer">Terms</a>.
                </label>
                <label className={s.check}>
                  <input type="checkbox" name="privacy" /> I accept the{' '}
                  <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                </label>
                <label className={s.check}>
                  <input type="checkbox" name="marketing" /> Send me product updates (optional).
                </label>
              </>
            )}

            {error && <div className={s.error}>{error}</div>}

            <Button type="submit" variant="primary" className={s.submit}>
              {pending ? 'Please wait…' : isRegister ? 'Create account' : 'Sign in'}
            </Button>
          </form>

          <div className={s.alt}>
            {isRegister ? (
              <>Already have an account? <Link to={`/login${search}`}>Sign in</Link></>
            ) : (
              <>New to MetaHub? <Link to={`/register${search}`}>Create an account</Link></>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
}
